import { Button } from "../components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
	Mail,
	MapPin,
	Award,
	Users,
	Clock,
	CheckCircle,
	Building2,
	Zap as Pipe,
	Loader2,
	CheckCircle2,
	AlertCircle,
} from "lucide-react";
import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { sendContactEmail } from "../utils/emailService";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { track } from "@vercel/analytics";
import { getLeadAttribution } from "../utils/leadAttribution";

interface ContactFormData {
	name: string;
	email: string;
	phone: string;
	company: string;
	project: string;
	message: string;
}

export const Contact = () => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [captchaToken, setCaptchaToken] = useState<string | null>(null);
	const turnstileRef = useRef<TurnstileInstance | null>(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<ContactFormData>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const onSubmit = async (data: ContactFormData) => {
		// Validate captcha before submitting
		if (!captchaToken) {
			toast.error("Please complete the CAPTCHA verification.");
			return;
		}

		setIsSubmitting(true);

		try {
			const leadSource = getLeadAttribution();
			// Google Analytics event tracking
			if (typeof window !== "undefined" && typeof window.gtag === "function") {
				window.gtag("event", "lead_form_submit", {
					event_category: "Lead Generation",
					event_label: data.project || "General Inquiry",
					value: 1,
				});
			}

			// Send email using the email service utility
			await sendContactEmail(data);

			track("estimate_form_submitted", {
				source: leadSource.utmSource,
				campaign: leadSource.utmCampaign,
			});

			toast.success(
				"Thank you for your inquiry! We will contact you within 24 hours."
			);
			setIsSubmitted(true);
			reset();
			setCaptchaToken(null);

			// Reset Turnstile widget
			if (turnstileRef.current) {
				turnstileRef.current.reset();
			}

			// Reset submitted state after 5 seconds
			setTimeout(() => setIsSubmitted(false), 5000);
		} catch (error) {
			console.error("Form submission error:", error);
			toast.error(
				"There was an error submitting your form. Please try again or contact us directly."
			);

			// Reset captcha on error so user can try again
			setCaptchaToken(null);
			if (turnstileRef.current) {
				turnstileRef.current.reset();
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const onError = () => {
		// Show error message when form validation fails
		toast.error(
			"Please fill in all required fields correctly before submitting."
		);
	};

	return (
		<section
			id="contact"
			className="py-24 w-full bg-gradient-to-br from-slate-50 via-blue-50 to-gray-50 flex justify-center overflow-x-clip px-4 md:px-8 lg:px-12"
		>
			<div className="relative container max-w-8xl mx-auto px-0 md:px-8 lg:px-16">
				<div className="flex flex-col items-center mb-16 px-2 md:px-0">
					<h2 className="text-4xl md:text-5xl font-bold tracking-tight text-navy mb-4 text-center">
						Contact Us
					</h2>
					<p className="text-lg text-muted-foreground max-w-2xl text-center">
						Get a free consultation and discover how our local expertise can
						streamline your project timeline and reduce costs.
					</p>
				</div>

				<div className="grid lg:grid-cols-2 gap-16 items-start">
					{/* Contact Form */}
					<Card className="shadow-xl border border-blue/20 flex flex-col justify-stretch">
						<CardHeader>
							<CardTitle className="text-xl">
								Get Your Free Consultation
							</CardTitle>
							<CardDescription>
								Fill out the form below and we'll contact you within 24 hours to
								discuss your project.
							</CardDescription>
						</CardHeader>
						<CardContent className="flex-1 flex flex-col justify-center">
							{isSubmitted ? (
								<div className="flex flex-col items-center justify-center py-12 text-center">
									<CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
									<h3 className="text-xl font-semibold text-green-700 mb-2">
										Thank You!
									</h3>
									<p className="text-muted-foreground mb-4">
										Your inquiry has been submitted successfully. We'll contact
										you within 24 hours.
									</p>
									<Button
										onClick={() => setIsSubmitted(false)}
										variant="outline"
									>
										Send Another Message
									</Button>
								</div>
							) : (
								<form
									onSubmit={handleSubmit(onSubmit, onError)}
									className="flex flex-col h-full justify-between space-y-4"
									autoComplete="on"
									noValidate
								>
									<p className="text-sm text-muted-foreground">
										Fields marked with <span className="text-red-500">*</span>{" "}
										are required
									</p>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div className="flex flex-col gap-2">
											<label htmlFor="name" className="sr-only">
												Name
											</label>
											<Input
												id="name"
												{...register("name", {
													required: "Name is required",
													minLength: {
														value: 2,
														message: "Name must be at least 2 characters",
													},
													maxLength: {
														value: 64,
														message: "Name must be less than 64 characters",
													},
												})}
												placeholder="Full Name *"
												autoComplete="name"
												className={`h-16 text-lg md:h-16 md:text-lg ${
													errors.name
														? "border-red-500 focus:border-red-500"
														: ""
												}`}
											/>
											{errors.name && (
												<div className="flex items-center gap-1 text-red-500 text-sm">
													<AlertCircle className="h-4 w-4" />
													{errors.name.message}
												</div>
											)}
										</div>
										<div className="flex flex-col gap-2">
											<label htmlFor="email" className="sr-only">
												Email
											</label>
											<Input
												id="email"
												type="email"
												{...register("email", {
													required: "Email is required",
													pattern: {
														value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
														message: "Please enter a valid email address",
													},
												})}
												placeholder="Email Address *"
												autoComplete="email"
												className={`h-16 text-lg md:h-16 md:text-lg ${
													errors.email
														? "border-red-500 focus:border-red-500"
														: ""
												}`}
											/>
											{errors.email && (
												<div className="flex items-center gap-1 text-red-500 text-sm">
													<AlertCircle className="h-4 w-4" />
													{errors.email.message}
												</div>
											)}
										</div>
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div className="flex flex-col gap-2">
											<label htmlFor="phone" className="sr-only">
												Phone
											</label>
											<Input
												id="phone"
												type="tel"
												{...register("phone", {
													required: "Phone number is required",
											pattern: {
												value: /^(?=(?:\D*\d){10,15}\D*$)[+\d\s().-]+$/,
												message: "Please enter a valid phone number, including area code",
													},
												})}
												placeholder="Phone Number *"
												autoComplete="tel"
												className={`h-16 text-lg md:h-16 md:text-lg ${
													errors.phone
														? "border-red-500 focus:border-red-500"
														: ""
												}`}
											/>
											{errors.phone && (
												<div className="flex items-center gap-1 text-red-500 text-sm">
													<AlertCircle className="h-4 w-4" />
													{errors.phone.message}
												</div>
											)}
										</div>
										<div className="flex flex-col gap-2">
											<label htmlFor="company" className="sr-only">
												Company
											</label>
											<Input
												id="company"
												{...register("company", {
													maxLength: {
														value: 64,
														message:
															"Company name must be less than 64 characters",
													},
												})}
												placeholder="Company (Optional)"
												autoComplete="organization"
												className={`h-16 text-lg md:h-16 md:text-lg ${
													errors.company
														? "border-red-500 focus:border-red-500"
														: ""
												}`}
											/>
											{errors.company && (
												<div className="flex items-center gap-1 text-red-500 text-sm">
													<AlertCircle className="h-4 w-4" />
													{errors.company.message}
												</div>
											)}
										</div>
									</div>

									<div className="flex flex-col gap-2">
										<label htmlFor="project" className="sr-only">
											Project Type
										</label>
										<Input
											id="project"
											{...register("project", {
												required: "Project type is required",
												minLength: {
													value: 2,
													message: "Project type must be at least 2 characters",
												},
												maxLength: {
													value: 64,
													message:
														"Project type must be less than 64 characters",
												},
											})}
											placeholder="Project Type (e.g., Civil Engineering, Site Development) *"
											className={`h-16 text-lg md:h-16 md:text-lg ${
												errors.project
													? "border-red-500 focus:border-red-500"
													: ""
											}`}
										/>
										{errors.project && (
											<div className="flex items-center gap-1 text-red-500 text-sm">
												<AlertCircle className="h-4 w-4" />
												{errors.project.message}
											</div>
										)}
									</div>

									<div className="flex flex-col gap-2">
										<label htmlFor="message" className="sr-only">
											Project Details
										</label>
										<Textarea
											id="message"
											{...register("message", {
												required: "Project details are required",
												minLength: {
													value: 10,
													message:
														"Please provide more details about your project (at least 10 characters)",
												},
												maxLength: {
													value: 1000,
													message: "Message must be less than 1000 characters",
												},
											})}
											placeholder="Tell us about your project requirements... *"
											rows={5}
											className={`h-40 text-lg md:h-40 md:text-lg ${
												errors.message
													? "border-red-500 focus:border-red-500"
													: ""
											}`}
										/>
										{errors.message && (
											<div className="flex items-center gap-1 text-red-500 text-sm">
												<AlertCircle className="h-4 w-4" />
												{errors.message.message}
											</div>
										)}
									</div>

									{/* Cloudflare Turnstile CAPTCHA */}
									<div className="flex justify-center">
										<Turnstile
											ref={turnstileRef}
											siteKey={
												process.env.REACT_APP_TURNSTILE_SITE_KEY ||
												"1x00000000000000000000AA"
											}
											onSuccess={(token) => setCaptchaToken(token)}
											onError={() => {
												setCaptchaToken(null);
												toast.error(
													"CAPTCHA verification failed. Please try again."
												);
											}}
											onExpire={() => {
												setCaptchaToken(null);
												toast.warning("CAPTCHA expired. Please verify again.");
											}}
											options={{
												theme: "light",
												size: "normal",
											}}
										/>
									</div>

									<Button
										type="submit"
										className="w-full"
										size="lg"
										disabled={isSubmitting || !captchaToken}
									>
										{isSubmitting ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												Sending...
											</>
										) : (
											"Get Free Consultation"
										)}
									</Button>
									<div className="text-xs text-muted-foreground mt-2 text-center">
										<span role="img" aria-label="lock">
											🔒
										</span>{" "}
										100% confidential. Your information will never be shared.
									</div>
								</form>
							)}
						</CardContent>
					</Card>
					{/* Contact Information */}
					<div className="space-y-8">
						<div>
							<h3 className="text-xl mb-4">Contact Information</h3>
							<div className="space-y-4">
								<div className="flex items-center gap-3">
									<Mail className="h-5 w-5 text-primary" />
									<div>
										<div className="text-lg">miguel@govaengineering.com</div>
										<div className="text-lg text-muted-foreground">
											24-hour email response
										</div>
									</div>
								</div>
								<div className="flex items-center gap-3">
									<MapPin className="h-5 w-5 text-primary" />
									<div>
										<div className="text-lg">Austin, Texas</div>
										<div className="text-lg text-muted-foreground">
											Serving Central Texas
										</div>
									</div>
								</div>
							</div>
						</div>

						<Card className="bg-gradient-to-br from-blue/5 to-blue/10 border border-blue/20">
							<CardContent className="p-6">
								<h4 className="mb-4 text-lg">Civil Engineering Specialists</h4>
								<div className="space-y-3">
									<div className="flex items-center gap-2">
										<Building2 className="h-4 w-4 text-blue" />
										<span className="text-lg">
											Expert site development &amp; design
										</span>
									</div>
									<div className="flex items-center gap-2">
										<Pipe className="h-4 w-4 text-blue" />
										<span className="text-lg">
											Infrastructure coordination &amp; planning
										</span>
									</div>
									<div className="flex items-center gap-2">
										<CheckCircle className="h-4 w-4 text-blue" />
										<span className="text-lg">Local permit coordination</span>
									</div>
									<div className="flex items-center gap-2">
										<Clock className="h-4 w-4 text-blue" />
										<span className="text-lg">
											Schedule-aware project planning
										</span>
									</div>
								</div>
							</CardContent>
						</Card>

						<div className="bg-secondary/30 p-6 rounded-lg border border-blue/10">
							<h4 className="mb-4 text-lg">Why Choose Gova Engineering?</h4>
							<div className="space-y-3">
								<div className="flex items-center gap-2">
									<Clock className="h-4 w-4 text-navy" />
									<span className="text-lg">Fast project turnaround times</span>
								</div>
								<div className="flex items-center gap-2">
									<Users className="h-4 w-4 text-navy" />
									<span className="text-lg">
										Local relationships with authorities
									</span>
								</div>
								<div className="flex items-center gap-2">
									<Award className="h-4 w-4 text-navy" />
									<span className="text-lg">
										Proven track record of success
									</span>
								</div>
								<div className="flex items-center gap-2">
									<CheckCircle className="h-4 w-4 text-navy" />
									<span className="text-lg">
										Comprehensive project management
									</span>
								</div>
							</div>
						</div>

						<Card className="border border-blue/20">
							<CardContent className="p-6 text-center">
								<h4 className="mb-2">Emergency Projects?</h4>
								<p className="text-sm text-muted-foreground mb-4">
									Need urgent engineering assistance? We offer expedited
									services for time-critical projects.
								</p>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</section>
	);
};
